// frontend/src/components/crm/Dashboard.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCRM } from '../../contexts/CRMContext';
import { PERMISSIONS, hasPermission } from '../../utils/permissions';
import { useBranding } from '../../contexts/BrandingContext';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { PipelineVisualization } from '../ui/pipeline-visualization';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { StatusBadge, StatusIndicator } from '../ui/status-components';
import { Separator } from '../ui/separator';
import { SmartDashboardCard } from '../ui/help-components';
import crmApi, { analyticsAPI, leadAPI, campaignsAPI, formsAPI, workflowAPI, calendarAPI, accountAPI, clientAPI, agencyAPI } from '../../utils/crmApi';
import toast from 'react-hot-toast';
import { useDialog, DialogHeader, DialogBody, DialogFooter } from '../kibo-ui/dialog';
import {
    UserGroupIcon,
    BuildingOfficeIcon,
    ClipboardDocumentListIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    ArrowPathIcon,
    EyeIcon,
    PlusIcon,
    CalendarIcon,
    ChartBarIcon,
    UserPlusIcon,
    PaperAirplaneIcon,
    DocumentTextIcon,
    BoltIcon,
    EnvelopeIcon,
    UserCircleIcon,
    ChatBubbleLeftRightIcon,
    InboxIcon
} from '@heroicons/react/24/outline';
import {
    EnhancedDonutChart,
    ChartLoadingState,
    ChartErrorState,
    getSemanticColors
} from '../ui/enhanced-charts';

const Dashboard = () => {
    const { currentAgency, user, agencies, createAgency, canCreateAgency, getRemainingAgencySlots, forceRefresh, switchAgency, loading: contextLoading, backendError } = useCRM();
    const { branding } = useBranding();
    const navigate = useNavigate();
    const { openDialog, closeDialog } = useDialog();
    const semanticColors = getSemanticColors(branding);
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [error, setError] = useState(null);
    const [additionalStats, setAdditionalStats] = useState({
        leads: null,
        campaigns: null,
        forms: null,
        formSubmissions: 0,
        workflowsExecuted: 0,
        upcomingEvents: 0,
        campaignEngagements: 0
    });
    const [trendData, setTrendData] = useState({});
    const [pipelineData, setPipelineData] = useState(null);
    const [pipelineTimeframe, setPipelineTimeframe] = useState('monthly'); // 'daily', 'weekly', 'monthly', 'yearly'
    const [teamMembers, setTeamMembers] = useState([]);
    const [recentActivities, setRecentActivities] = useState([]);
    const [collaborationOverview, setCollaborationOverview] = useState(null);
    const [recentChannelMessages, setRecentChannelMessages] = useState([]);
    
    // Combined loading state: wait for context to load AND dashboard data to load
    const isLoading = contextLoading || loading;
    
    // Check user permissions
    const currentAgencyId = currentAgency?._id || currentAgency?.id;
    const canViewAnalytics = hasPermission(user, PERMISSIONS.VIEW_ANALYTICS, currentAgencyId);
    const canViewReports = hasPermission(user, PERMISSIONS.VIEW_REPORTS, currentAgencyId);
    const canCreateClient = hasPermission(user, PERMISSIONS.CREATE_CLIENT, currentAgencyId);
    const canCreateTask = hasPermission(user, PERMISSIONS.CREATE_TASK, currentAgencyId);
    const canScheduleMeeting = hasPermission(user, PERMISSIONS.SCHEDULE_MEETING, currentAgencyId);

    // Handle creating a new agency (using dialog, same as dropdown)
    const handleCreateAgency = useCallback(() => {
        if (!canCreateAgency()) {
            toast.error('You have reached the maximum number of agencies for your plan');
            return;
        }

        const dialogId = openDialog({
            isOpen: true,
            children: (
                <div role="dialog" aria-modal="true">
                    <DialogHeader title="Create New Agency" onClose={() => closeDialog(dialogId)} />
                    <DialogBody>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Agency Name</label>
                                <input
                                    type="text"
                                    id="dashboardAgencyName"
                                    className="w-full px-3 py-2 border border-input rounded-md b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 f focus:ring-2 focus:ring-ring focus:border-ringocus:ring-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                    placeholder="Enter agency name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                                <textarea
                                    id="dashboardAgencyDescription"
                                    rows={3}
                                    className="w-full px-3 py-2 border border-input rounded-md b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 f focus:ring-2 focus:ring-ring focus:border-ringocus:ring-ring"
                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                    placeholder="Enter agency description"
                                />
                            </div>
                            {agencies.length > 0 && (
                                <div className="flex items-center justify-between text-sm text-muted-foreground">
                                    <span>Remaining slots: {getRemainingAgencySlots()}</span>
                                    <span>Current agencies: {agencies.length}</span>
                                </div>
                            )}
                        </div>
                    </DialogBody>
                    <DialogFooter>
                        <button
                            type="button"
                            className="px-3 py-2 text-sm border rounded-md"
                            onClick={() => closeDialog(dialogId)}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="px-3 py-2 text-sm text-white rounded-md"
                            style={{ backgroundColor: branding.primaryColor }}
                            onClick={async () => {
                                const name = document.getElementById('dashboardAgencyName').value;
                                const description = document.getElementById('dashboardAgencyDescription').value;
                                if (!name?.trim()) {
                                    toast.error('Agency name is required');
                                    return;
                                }
                                try {
                                    const result = await createAgency({
                                        name: name.trim(),
                                        description: description.trim(),
                                        motto: '',
                                        email: user?.email || '',
                                        phone: '',
                                        address: '',
                                        website: ''
                                    });
                                    if (result.success) {
                                        toast.success('Agency created successfully!');
                                        try {
                                            // Refresh user/agencies to ensure membership is present
                                            if (typeof forceRefresh === 'function') {
                                                await forceRefresh();
                                            }
                                            const newId = result.data?._id || result.data?.id;
                                            if (newId) {
                                                await switchAgency(String(newId));
                                            }
                                        } catch (_) {}
                                        closeDialog(dialogId);
                                    } else {
                                        toast.error(result.error || 'Failed to create agency');
                                    }
                                } catch (error) {
                                    console.error('Failed to create agency:', error);
                                    toast.error('Failed to create agency');
                                }
                            }}
                        >
                            Create Agency
                        </button>
                    </DialogFooter>
                </div>
            )
        });
    }, [canCreateAgency, getRemainingAgencySlots, agencies.length, branding.primaryColor, user?.email, createAgency, forceRefresh, switchAgency, openDialog, closeDialog]);

    // Fetch real dashboard data
    useEffect(() => {
        const fetchDashboardData = async () => {
            // Wait for context to finish loading before checking for agency
            if (contextLoading) {
                return;
            }
            
            // Don't fetch if there's no agency (only after context has loaded)
            if (!currentAgency) {
                setLoading(false);
                return;
            }

            // Always fetch digest; fetch analytics only if permitted

            try {
                setLoading(true);
                setError(null);
                const [analyticsRes, digestRes, statsRes, leadsStatsRes, campaignsRes, formsRes] = await Promise.allSettled([
                    canViewAnalytics ? analyticsAPI.getDashboard() : Promise.resolve(null),
                    crmApi.get('/digest').then(r => r.data).catch(() => null),
                    agencyAPI.getStats(currentAgencyId).then(r => r.data).catch(() => null),
                    leadAPI.getStats().catch(() => null),
                    campaignsAPI.getAll({ limit: 1000 }).catch(() => null),
                    formsAPI.getAll({ limit: 1000 }).catch(() => null)
                ]);

                if (canViewAnalytics && analyticsRes.status === 'fulfilled' && analyticsRes.value?.data?.success) {
                    const response = analyticsRes.value;
                    const data = response.data.data;
                    
                    // Transform API data to match component expectations
                    const transformedData = {
                        stats: {
                            totalClients: data.summary.clients.total,
                            activeAccounts: data.summary.accounts.active,
                            activeTasks: data.summary.tasks.total - data.summary.tasks.completed,
                            overdueTasks: data.summary.tasks.overdue,
                            newClientsThisMonth: data.summary.clients.newThisMonth,
                            completedTasks: data.summary.tasks.completed,
                            totalAccounts: data.summary.accounts.total
                        },
                        clientStatusData: [
                            { name: 'Active', value: data.summary.clients.active, color: semanticColors.client.active },
                            { name: 'Prospect', value: data.summary.clients.prospect, color: semanticColors.client.prospect },
                            { name: 'Completed', value: data.summary.clients.completed, color: semanticColors.client.completed },
                            { name: 'Inactive', value: data.summary.clients.total - data.summary.clients.active - data.summary.clients.prospect - data.summary.clients.completed, color: semanticColors.client.inactive }
                        ],
                        taskStatusData: [
                            { name: 'Completed', value: data.summary.tasks.completed, color: semanticColors.task.completed },
                            { name: 'In Progress', value: data.summary.tasks.inProgress, color: semanticColors.task.in_progress },
                            { name: 'Pending', value: data.summary.tasks.total - data.summary.tasks.completed - data.summary.tasks.inProgress - data.summary.tasks.overdue, color: semanticColors.task.pending },
                            { name: 'Overdue', value: data.summary.tasks.overdue, color: semanticColors.task.overdue }
                        ],
                        recentClients: data.recent.clients || [],
                        recentTasks: data.recent.tasks || []
                    };
                    
                    setDashboardData(transformedData);
                }

                // Attach digest to dashboardData (non-blocking)
                if (digestRes.status === 'fulfilled' && digestRes.value?.success) {
                    setDashboardData(prev => ({ ...(prev || {}), digest: digestRes.value.data }));
                }

                // Attach agency stats to reinforce account counts (treat 'Customer' as active)
                if (statsRes.status === 'fulfilled' && statsRes.value?.success) {
                    const s = statsRes.value.data || {};
                    setDashboardData(prev => {
                        const next = { ...(prev || {}), stats: { ...(prev?.stats || {}) } };
                        if (typeof s.accounts?.total === 'number') {
                            next.stats.totalAccounts = s.accounts.total;
                        }
                        // Prefer backend-provided activeAccounts if present; otherwise, if there is exactly one account
                        // and its status is 'customer' in other parts of the app, we treat it as active by minimum 1.
                        if (typeof s.accounts?.active === 'number') {
                            next.stats.activeAccounts = s.accounts.active;
                        }
                        // Ensure active <= total and non-negative
                        if (typeof next.stats.activeAccounts === 'number' && typeof next.stats.totalAccounts === 'number') {
                            next.stats.activeAccounts = Math.max(0, Math.min(next.stats.activeAccounts, next.stats.totalAccounts));
                        }
                        return next;
                    });
                }

                // Process additional stats (leads, campaigns, forms, workflows, events)
                const leadsStats = leadsStatsRes.status === 'fulfilled' && leadsStatsRes.value?.data?.success 
                    ? leadsStatsRes.value.data.data 
                    : null;
                
                const campaignsData = campaignsRes.status === 'fulfilled' && campaignsRes.value?.data?.success
                    ? campaignsRes.value.data.data || []
                    : [];
                
                const formsData = formsRes.status === 'fulfilled' && formsRes.value?.data?.success
                    ? formsRes.value.data.data || []
                    : [];

                // Fetch workflows and calendar events
                const [workflowsRes, upcomingEventsRes] = await Promise.allSettled([
                    workflowAPI.getAll({ status: 'active' }),
                    calendarAPI.getUpcoming()
                ]);

                // Calculate form submissions for active/published forms
                let formSubmissions = 0;
                try {
                    const publishedForms = formsData.filter(f => f.status === 'published' || f.isPublished);
                    const submissionCounts = await Promise.allSettled(
                        publishedForms.map(form => formsAPI.getSubmissions(form._id || form.id))
                    );
                    formSubmissions = submissionCounts.reduce((sum, res) => {
                        if (res.status === 'fulfilled' && res.value?.data?.success) {
                            return sum + (res.value.data.data?.length || 0);
                        }
                        return sum;
                    }, 0);
                } catch (e) {
                    console.error('Error fetching form submissions:', e);
                }

                // Calculate workflows executed (sum of totalExecutions from all workflows)
                let workflowsExecuted = 0;
                if (workflowsRes.status === 'fulfilled' && workflowsRes.value?.data?.success) {
                    const workflows = workflowsRes.value.data.data || [];
                    workflowsExecuted = workflows.reduce((sum, w) => {
                        return sum + (w.totalExecutions || 0);
                    }, 0);
                }

                // Count upcoming events this week
                let upcomingEvents = 0;
                if (upcomingEventsRes.status === 'fulfilled' && upcomingEventsRes.value?.data?.success) {
                    const now = new Date();
                    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
                    const events = upcomingEventsRes.value.data.data || [];
                    upcomingEvents = events.filter(e => {
                        const eventDate = new Date(e.startAt);
                        return eventDate >= now && eventDate <= weekFromNow;
                    }).length;
                }

                // Calculate campaign engagements (sum of clicks, opens, replies from campaigns)
                // Include running, scheduled, and completed campaigns (they still have engagement data)
                let campaignEngagements = 0;
                try {
                    const activeCampaigns = campaignsData.filter(c => 
                        c.status === 'running' || c.status === 'scheduled' || c.status === 'completed'
                    );
                    const analyticsPromises = activeCampaigns.map(campaign => 
                        campaignsAPI.getAnalytics(campaign._id || campaign.id).catch(() => null)
                    );
                    const analyticsResults = await Promise.allSettled(analyticsPromises);
                    
                    campaignEngagements = analyticsResults.reduce((sum, res) => {
                        if (res.status === 'fulfilled' && res.value?.data?.success) {
                            const analytics = res.value.data.data || {};
                            const overview = analytics.overview || {};
                            // Use the correct field names from the analytics response
                            // uniqueClicked = total unique clicks
                            // uniqueOpened = total unique opens
                            // replied = total replies
                            return sum + (overview.uniqueClicked || 0) + (overview.uniqueOpened || 0) + (overview.replied || 0);
                        }
                        return sum;
                    }, 0);
                } catch (e) {
                    console.error('Error fetching campaign analytics:', e);
                }

                setAdditionalStats({
                    leads: leadsStats,
                    campaigns: {
                        total: campaignsData.length,
                        active: campaignsData.filter(c => c.status === 'running' || c.status === 'scheduled').length,
                        draft: campaignsData.filter(c => c.status === 'draft').length,
                        completed: campaignsData.filter(c => c.status === 'completed').length
                    },
                    forms: {
                        total: formsData.length,
                        published: formsData.filter(f => f.status === 'published' || f.isPublished).length,
                        draft: formsData.filter(f => f.status === 'draft' || !f.isPublished).length
                    },
                    formSubmissions,
                    workflowsExecuted,
                    upcomingEvents,
                    campaignEngagements
                });
            } catch (err) {
                console.error('Dashboard data fetch error:', err);
                setError('Failed to load dashboard data');
                toast.error('Failed to load dashboard data');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [currentAgency, canViewAnalytics, contextLoading]);

    // Fetch team members
    useEffect(() => {
        if (!currentAgency?._id || !canViewAnalytics) return;
        
        const fetchTeamMembers = async () => {
            try {
                const response = await agencyAPI.getMembers(currentAgency._id);
                const data = response.data;
                if (data.success && Array.isArray(data.members)) {
                    // Filter to show only staff members (agency_admin and agency_user) with active status
                    // This matches TeamManagement's filtering logic for seat counting
                    const activeStaffMembers = data.members
                        .filter(m => m.status === 'active' && (m.role === 'agency_admin' || m.role === 'agency_user'))
                        .slice(0, 5);
                    setTeamMembers(activeStaffMembers);
                }
            } catch (error) {
                console.error('Error fetching team members:', error);
            }
        };
        
        fetchTeamMembers();
    }, [currentAgency, canViewAnalytics]);

    // Build recent activities from dashboard data
    useEffect(() => {
        if (!dashboardData) return;
        
        const activities = [];
        
        // Add recent clients
        if (dashboardData.recentClients) {
            dashboardData.recentClients.forEach(client => {
                activities.push({
                    id: `client-${client._id || client.id}`,
                    type: 'client',
                    icon: UserGroupIcon,
                    title: `New client: ${`${client.firstName || ''} ${client.lastName || ''}`.trim() || client.name || 'Unknown'}`,
                    description: client.email || 'No email',
                    timestamp: new Date(client.createdAt || Date.now()),
                    color: 'text-blue-600'
                });
            });
        }
        
        // Add recent tasks
        if (dashboardData.recentTasks) {
            dashboardData.recentTasks.forEach(task => {
                activities.push({
                    id: `task-${task._id || task.id}`,
                    type: 'task',
                    icon: ClipboardDocumentListIcon,
                    title: task.title || 'Untitled Task',
                    description: task.clientId?.name || 'No client assigned',
                    timestamp: new Date(task.createdAt || Date.now()),
                    color: task.status === 'completed' ? 'text-green-600' : 'text-orange-600'
                });
            });
        }
        
        // Sort by timestamp (most recent first) and limit to 10
        activities.sort((a, b) => b.timestamp - a.timestamp);
        setRecentActivities(activities.slice(0, 10));
    }, [dashboardData]);

    // Fetch Collaboration Hub overview and recent messages
    useEffect(() => {
        if (!currentAgency?._id || !canViewAnalytics) return;
        
        const fetchCollaborationData = async () => {
            try {
                // Fetch overview
                const overviewRes = await crmApi.get('/collaboration/channels/overview');
                if (overviewRes.data?.success) {
                    setCollaborationOverview(overviewRes.data.data);
                }
                
                // Fetch recent messages from all channels
                const channelsRes = await crmApi.get('/collaboration/channels');
                if (channelsRes.data?.success && Array.isArray(channelsRes.data.data)) {
                    // Get recent messages from first few channels
                    const recentMessages = [];
                    for (const channel of channelsRes.data.data.slice(0, 3)) {
                        try {
                            const messagesRes = await crmApi.get(`/collaboration/channels/${channel._id}/messages`, {
                                params: { limit: 3 }
                            });
                            if (messagesRes.data?.success && messagesRes.data.data?.messages) {
                                messagesRes.data.data.messages.forEach(msg => {
                                    recentMessages.push({
                                        ...msg,
                                        channelName: channel.name,
                                        channelType: channel.type
                                    });
                                });
                            }
                        } catch (err) {
                            console.error(`Error fetching messages for channel ${channel._id}:`, err);
                        }
                    }
                    // Sort by timestamp and limit to 5
                    recentMessages.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                    setRecentChannelMessages(recentMessages.slice(0, 5));
                }
            } catch (error) {
                console.error('Error fetching collaboration data:', error);
            }
        };
        
        fetchCollaborationData();
    }, [currentAgency, canViewAnalytics]);

    // Generate trend data for past 30 days (mock data based on current values)
    const generateTrendData = useCallback((currentValue, metricName) => {
        const days = 30;
        const data = [];
        const now = new Date();
        
        // Generate trend with some variation
        for (let i = days - 1; i >= 0; i--) {
            const date = new Date(now);
            date.setDate(date.getDate() - i);
            
            // Create a trend that gradually increases/decreases to current value
            const progress = (days - i) / days;
            const variation = 0.1; // 10% random variation
            const baseValue = currentValue * progress;
            const randomVariation = baseValue * variation * (Math.random() - 0.5);
            const value = Math.max(0, Math.round(baseValue + randomVariation));
            
            data.push({
                date: date.toISOString().split('T')[0],
                value: value
            });
        }
        
        return data;
    }, []);

        // Fetch pipeline data - full sales pipeline from leads to clients
        useEffect(() => {
            if (!currentAgency || !canViewAnalytics) return;
            
            const fetchPipelineData = async () => {
                try {
                    // Calculate date range based on timeframe
                    const now = new Date();
                    let startDate = new Date();
                    
                    switch (pipelineTimeframe) {
                        case 'daily':
                            startDate.setDate(now.getDate() - 1);
                            break;
                        case 'weekly':
                            startDate.setDate(now.getDate() - 7);
                            break;
                        case 'monthly':
                            startDate.setMonth(now.getMonth() - 1);
                            break;
                        case 'yearly':
                            startDate.setFullYear(now.getFullYear() - 1);
                            break;
                        default:
                            startDate.setMonth(now.getMonth() - 1);
                    }
                    
                    // Fetch all pipeline data in parallel
                    const [leadsRes, accountsRes, clientsRes] = await Promise.allSettled([
                        leadAPI.getAll({ limit: 1000 }),
                        accountAPI.getAll({ limit: 1000 }),
                        clientAPI.getAll({ limit: 1000 })
                    ]);
                
                    const leads = leadsRes.status === 'fulfilled' && leadsRes.value?.data?.success
                        ? leadsRes.value.data.data || []
                        : [];
                    
                    const accounts = accountsRes.status === 'fulfilled' && accountsRes.value?.data?.success
                        ? accountsRes.value.data.data || []
                        : [];
                    
                    const clients = clientsRes.status === 'fulfilled' && clientsRes.value?.data?.success
                        ? clientsRes.value.data.data || []
                        : [];
                    
                    // Filter data by timeframe (based on createdAt date)
                    const filterByTimeframe = (items) => {
                        return items.filter(item => {
                            const itemDate = new Date(item.createdAt || item.updatedAt || Date.now());
                            return itemDate >= startDate;
                        });
                    };
                    
                    // Build pipeline stages in order: Leads → Contacted → Qualified → Proposal → Negotiation → Closed-Won
                    const pipeline = [];
                    
                    // Stage 1: Leads (new leads) - filtered by timeframe
                    const allNewLeads = leads.filter(l => (l.status || '').toLowerCase() === 'new');
                    const newLeads = filterByTimeframe(allNewLeads);
                    pipeline.push({
                        stage: 'new',
                        name: 'Leads',
                        count: newLeads.length,
                        value: newLeads.reduce((sum, l) => sum + (l.estimatedValue || 0), 0)
                    });
                    
                    // Stage 2: Contacted - filtered by timeframe
                    const allContactedLeads = leads.filter(l => (l.status || '').toLowerCase() === 'contacted');
                    const contactedLeads = filterByTimeframe(allContactedLeads);
                    pipeline.push({
                        stage: 'contacted',
                        name: 'Contacted',
                        count: contactedLeads.length,
                        value: contactedLeads.reduce((sum, l) => sum + (l.estimatedValue || 0), 0)
                    });
                    
                    // Stage 3: Qualified - filtered by timeframe
                    const allQualifiedLeads = leads.filter(l => (l.status || '').toLowerCase() === 'qualified');
                    const qualifiedLeads = filterByTimeframe(allQualifiedLeads);
                    pipeline.push({
                        stage: 'qualified',
                        name: 'Qualified',
                        count: qualifiedLeads.length,
                        value: qualifiedLeads.reduce((sum, l) => sum + (l.estimatedValue || 0), 0)
                    });
                    
                    // Stage 4: Proposal (from accounts or opportunities) - filtered by timeframe
                    const allProposalAccounts = accounts.filter(a => (a.stage || '').toLowerCase() === 'proposal');
                    const proposalAccounts = filterByTimeframe(allProposalAccounts);
                    pipeline.push({
                        stage: 'proposal',
                        name: 'Proposal',
                        count: proposalAccounts.length,
                        value: proposalAccounts.reduce((sum, a) => sum + (a.estimatedValue || 0), 0)
                    });
                    
                    // Stage 5: Negotiation - filtered by timeframe
                    const allNegotiationAccounts = accounts.filter(a => (a.stage || '').toLowerCase() === 'negotiation');
                    const negotiationAccounts = filterByTimeframe(allNegotiationAccounts);
                    pipeline.push({
                        stage: 'negotiation',
                        name: 'Negotiation',
                        count: negotiationAccounts.length,
                        value: negotiationAccounts.reduce((sum, a) => sum + (a.estimatedValue || 0), 0)
                    });
                    
                    // Stage 6: Closed-Won (converted leads or active clients) - filtered by timeframe
                    const allConvertedLeads = leads.filter(l => (l.status || '').toLowerCase() === 'converted');
                    const convertedLeads = filterByTimeframe(allConvertedLeads);
                    const allActiveClients = clients.filter(c => 
                        (c.status || '').toLowerCase() === 'active' || 
                        (c.status || '').toLowerCase() === 'customer'
                    );
                    const activeClients = filterByTimeframe(allActiveClients);
                    const closedWonCount = convertedLeads.length + activeClients.length;
                    pipeline.push({
                        stage: 'closed_won',
                        name: 'Closed-Won',
                        count: closedWonCount,
                        value: activeClients.reduce((sum, c) => sum + (c.totalRevenue || 0), 0)
                    });
                    
                    setPipelineData(pipeline);
            } catch (e) {
                console.error('Error fetching pipeline data:', e);
            }
        };
        
                fetchPipelineData();
            }, [currentAgency, canViewAnalytics, pipelineTimeframe]);

    // Helper function to get contextual help text for dashboard stats
    const getHelpTextForStat = (statName) => {
        const helpTexts = {
            'Total Clients': 'Shows all clients in your CRM. Click to view detailed client list and manage relationships.',
            'Active Accounts': 'Displays accounts with ongoing relocation processes. These are your primary revenue sources.',
            'Active Tasks': 'Tasks currently in progress. Monitor these to ensure timely completion of relocation steps.',
            'Overdue Tasks': 'Tasks past their due date. These need immediate attention to maintain client satisfaction.',
            'Total Leads': 'All potential clients in your pipeline. Track conversion rates and engagement levels.',
            'Form Submissions': 'Total submissions received from all active forms. Track engagement and conversion.',
            'Workflows Executed': 'Total number of automated workflows that have been executed. Monitor automation activity.',
            'Events This Week': 'Number of calendar events scheduled for the next 7 days. Stay organized and prepared.',
            'Campaign Engagements': 'Total interactions with your email campaigns including clicks, opens, and replies.',
            'Monthly Revenue': 'Total revenue generated this month from completed relocations and services.',
            'Conversion Rate': 'Percentage of prospects that successfully convert to paying clients.',
            'Average Deal Size': 'Average value of completed relocation packages.',
            'Client Satisfaction': 'Average rating from client feedback surveys and reviews.'
        };
        return helpTexts[statName] || 'Click to view more details about this metric.';
    };

    // Provide fallback data structure
    const stats = dashboardData?.stats || {
        totalClients: 0,
        activeAccounts: 0,
        activeTasks: 0,
        overdueTasks: 0,
        newClientsThisMonth: 0,
        completedTasks: 0,
        totalAccounts: 0
    };

    // Organize stat cards into categories for better UX
    const pipelineCards = [];
    const serviceCards = [];
    const activityCards = [];

    // Pipeline Category: Leads → Accounts → Clients
    if (additionalStats.leads) {
        const leads = additionalStats.leads;
        pipelineCards.push({
            name: 'Total Leads',
            value: leads.total || 0,
            change: leads.converted ? `${leads.converted} converted` : 'No conversions yet',
            changeType: leads.converted > 0 ? 'positive' : 'neutral',
            icon: UserPlusIcon,
            color: 'indigo',
            link: '/leads',
            trendData: generateTrendData(leads.total || 0, 'leads'),
            metricType: 'count',
            chartType: 'line' // Line chart for cleaner look on count metrics
        });
    }

    pipelineCards.push({
        name: 'Active Accounts',
        value: stats.activeAccounts,
        change: `of ${stats.totalAccounts} total`,
        changeType: 'neutral',
        icon: BuildingOfficeIcon,
        color: 'purple',
        link: '/accounts',
        trendData: generateTrendData(stats.activeAccounts, 'accounts'),
        metricType: 'count',
        chartType: 'line' // Line chart for count metrics
    });

    pipelineCards.push({
        name: 'Total Clients',
        value: stats.totalClients,
        change: `+${stats.newClientsThisMonth} this month`,
        changeType: stats.newClientsThisMonth > 0 ? 'positive' : 'neutral',
        changeValue: stats.newClientsThisMonth,
        changeLabel: 'This Month',
        icon: UserGroupIcon,
        color: 'blue',
        link: '/clients',
        trendData: generateTrendData(stats.totalClients, 'clients'),
        metricType: 'cumulative', // Cumulative growth over time
        chartType: 'area' // Area chart shows growth volume better
    });

    // Service Delivery Category: Tasks
    serviceCards.push({
        name: 'Active Tasks',
        value: stats.activeTasks,
        change: `${stats.completedTasks} completed`,
        changeType: stats.completedTasks > 0 ? 'positive' : 'neutral',
        changeValue: stats.completedTasks,
        changeLabel: 'Completed',
        icon: ClipboardDocumentListIcon,
        color: 'green',
        link: '/tasks',
        trendData: generateTrendData(stats.activeTasks, 'activeTasks'),
        metricType: 'count',
        chartType: 'line' // Line chart for task count
    });

    serviceCards.push({
        name: 'Overdue Tasks',
        value: stats.overdueTasks,
        change: 'Needs attention',
        changeType: 'negative',
        icon: ExclamationCircleIcon,
        color: 'red',
        link: '/tasks?filter=overdue',
        trendData: generateTrendData(stats.overdueTasks, 'overdueTasks'),
        metricType: 'discrete', // Discrete count that needs attention
        chartType: 'bar' // Bar chart emphasizes discrete values for urgency
    });

    // Activity & Engagement Category
    if (additionalStats.formSubmissions !== undefined) {
        activityCards.push({
            name: 'Form Submissions',
            value: additionalStats.formSubmissions,
            change: additionalStats.forms?.published ? `from ${additionalStats.forms.published} active forms` : 'No active forms',
            changeType: additionalStats.formSubmissions > 0 ? 'positive' : 'neutral',
            icon: DocumentTextIcon,
            color: 'amber',
            link: '/forms',
            trendData: generateTrendData(additionalStats.formSubmissions, 'formSubmissions'),
            metricType: 'aggregate', // Aggregated submissions over time
            chartType: 'area' // Area chart shows submission volume
        });
    }

    if (additionalStats.workflowsExecuted !== undefined) {
        activityCards.push({
            name: 'Workflows Executed',
            value: additionalStats.workflowsExecuted,
            change: 'Total executions',
            changeType: 'positive',
            icon: BoltIcon,
            color: 'orange',
            link: '/workflows',
            trendData: generateTrendData(additionalStats.workflowsExecuted, 'workflowsExecuted'),
            metricType: 'cumulative', // Cumulative executions
            chartType: 'area' // Area chart shows cumulative growth
        });
    }

    if (additionalStats.upcomingEvents !== undefined) {
        activityCards.push({
            name: 'Events This Week',
            value: additionalStats.upcomingEvents,
            change: 'Upcoming',
            changeType: 'neutral',
            icon: CalendarIcon,
            color: 'cyan',
            link: '/calendar',
            trendData: generateTrendData(additionalStats.upcomingEvents, 'upcomingEvents'),
            metricType: 'discrete', // Weekly discrete count
            chartType: 'bar' // Bar chart for weekly discrete data
        });
    }

    if (additionalStats.campaignEngagements !== undefined) {
        activityCards.push({
            name: 'Campaign Engagements',
            value: additionalStats.campaignEngagements,
            change: 'Clicks, opens & replies',
            changeType: additionalStats.campaignEngagements > 0 ? 'positive' : 'neutral',
            icon: EnvelopeIcon,
            color: 'purple',
            link: '/campaigns',
            trendData: generateTrendData(additionalStats.campaignEngagements, 'campaignEngagements'),
            metricType: 'aggregate', // Aggregated engagement metrics
            chartType: 'area' // Area chart shows engagement volume
        });
    }

    // Color functions removed - now using StatusBadge components

    // Show skeleton while context or dashboard data is loading
    if (isLoading) {
        return (
            <div className="space-y-6">
                {/* Welcome Banner Skeleton */}
                <Card className="relative overflow-hidden border-0">
                    <div className="bg-gradient-to-r from-gray-200 to-gray-300 p-6 animate-pulse">
                        <div className="h-8 bg-gray-300 rounded w-1/3 mb-2"></div>
                        <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                    </div>
                </Card>

                {/* Stats Grid Skeleton - Matching new compact card design */}
                <div className="space-y-6">
                    {/* Pipeline Section Skeleton */}
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="h-6 bg-muted rounded w-32 animate-pulse"></div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Pipeline Visualization Skeleton */}
                            <div className="space-y-3">
                                {[...Array(5)].map((_, i) => (
                                    <div key={i} className="flex items-center gap-4">
                                        <div className="w-24 h-4 bg-muted rounded animate-pulse"></div>
                                        <div className="flex-1 h-6 bg-muted rounded-md animate-pulse"></div>
                                        <div className="w-20 h-5 bg-muted rounded animate-pulse"></div>
                                    </div>
                                ))}
                            </div>
                            
                            {/* Pipeline Cards Skeleton */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {[...Array(3)].map((_, i) => (
                                    <Card key={i} className="animate-pulse">
                                        <div className="p-4 space-y-3">
                                            <div className="flex items-start justify-between">
                                                <div className="h-9 w-9 bg-muted rounded-lg"></div>
                                                <div className="h-4 w-8 bg-muted rounded"></div>
                                            </div>
                                            <div>
                                                <div className="h-8 bg-muted rounded w-20 mb-1"></div>
                                                <div className="h-3 bg-muted rounded w-24"></div>
                                            </div>
                                            <div className="h-14 bg-muted rounded -mx-4 -mb-4"></div>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Service Delivery Section Skeleton */}
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="h-6 bg-muted rounded w-40 animate-pulse"></div>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {[...Array(2)].map((_, i) => (
                                    <Card key={i} className="animate-pulse">
                                        <div className="p-4 space-y-3">
                                            <div className="flex items-start justify-between">
                                                <div className="h-9 w-9 bg-muted rounded-lg"></div>
                                                <div className="h-4 w-8 bg-muted rounded"></div>
                                            </div>
                                            <div>
                                                <div className="h-8 bg-muted rounded w-16 mb-1"></div>
                                                <div className="h-3 bg-muted rounded w-28"></div>
                                            </div>
                                            <div className="h-14 bg-muted rounded -mx-4 -mb-4"></div>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Activity & Engagement Section Skeleton */}
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="h-6 bg-muted rounded w-48 animate-pulse"></div>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {[...Array(4)].map((_, i) => (
                                    <Card key={i} className="animate-pulse">
                                        <div className="p-4 space-y-3">
                                            <div className="flex items-start justify-between">
                                                <div className="h-9 w-9 bg-muted rounded-lg"></div>
                                                <div className="h-4 w-8 bg-muted rounded"></div>
                                            </div>
                                            <div>
                                                <div className="h-8 bg-muted rounded w-20 mb-1"></div>
                                                <div className="h-3 bg-muted rounded w-32"></div>
                                            </div>
                                            <div className="h-14 bg-muted rounded -mx-4 -mb-4"></div>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Only show error state if there's an actual error AND user has analytics permission
    // (If user doesn't have analytics permission, !dashboardData is expected and not an error)
    if (error && canViewAnalytics) {
        return (
            <div className="space-y-6">
                {/* Welcome Banner */}
                <Card className="relative overflow-hidden border-0 text-white">
                    {branding.agencyCoverImage ? (
                        <div className="relative">
                            <img
                                src={branding.agencyCoverImage}
                                alt="Agency Cover"
                                className="w-full h-48 object-cover"
                            />
                            <div className="absolute inset-0 bg-black/40"></div>
                            <div className="absolute inset-0 p-6 flex items-center justify-between">
                                <div>
                                    <h1 className="text-3xl font-bold mb-2">
                                        Welcome back, {user?.name || 'User'}! 👋
                                    </h1>
                                    <p className="text-white/90">
                                        Here's what's happening with {currentAgency?.name || 'your agency'} today.
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-gradient-to-r from-primary to-purple-600 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h1 className="text-3xl font-bold mb-2 text-white">
                                        Welcome back, {user?.name || 'User'}! 👋
                                    </h1>
                                    <p className="text-white/90">
                                        Here's what's happening with {currentAgency?.name || 'your agency'} today.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </Card>

                {/* Error State */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ChartErrorState 
                        error={error || 'Failed to load dashboard data'} 
                        onRetry={() => window.location.reload()}
                        height={350}
                    />
                    <ChartErrorState 
                        error="Unable to load chart data" 
                        onRetry={() => window.location.reload()}
                        height={350}
                    />
                </div>
            </div>
        );
    }

    // Show backend error state (when backend is unreachable)
    if (backendError && !isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-background">
                <div className="max-w-md w-full text-center p-8">
                    <div className="flex justify-center mb-6">
                        <div className="bg-red-100 dark:bg-red-900/20 p-4 rounded-full">
                            <ExclamationTriangleIcon className="h-12 w-12 text-red-600 dark:text-red-400" />
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold text-foreground mb-4">
                        Connection Error
                    </h2>
                    <p className="text-muted-foreground mb-6">
                        {backendError}
                    </p>
                    <div className="space-y-3">
                        <button
                            onClick={() => window.location.reload()}
                            className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: branding.primaryColor }}
                        >
                            <ArrowPathIcon className="h-5 w-5 mr-2" />
                            Retry Connection
                        </button>
                        <button
                            onClick={() => {
                                localStorage.removeItem('crm_token');
                                localStorage.removeItem('current_agency_id');
                                window.location.href = '/login';
                            }}
                            className="w-full inline-flex items-center justify-center px-6 py-3 border border-border rounded-lg shadow-sm text-sm font-medium text-foreground bg-background hover:bg-muted transition-colors"
                        >
                            Go to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Show "no agency" state (only after all loading is complete and no backend error)
    if (!isLoading && !currentAgency && !error && !backendError) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-background">
                <div className="max-w-md w-full text-center p-8">
                    <div className="flex justify-center mb-6">
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 rounded-full">
                            <BuildingOfficeIcon className="h-12 w-12 text-white" />
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold text-foreground mb-4">
                        No Agency Found
                    </h2>
                    <p className="text-muted-foreground mb-6">
                        You're not currently part of any agency. You can create your own agency or wait to be invited to one.
                    </p>
                    <div className="space-y-3">
                        <button
                            onClick={handleCreateAgency}
                            className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: branding.primaryColor }}
                        >
                            <BuildingOfficeIcon className="h-5 w-5 mr-2" />
                            Create Your Agency
                        </button>
                        <button
                            onClick={() => navigate('/settings')}
                            className="w-full inline-flex items-center justify-center px-6 py-3 border border-border rounded-lg shadow-sm text-sm font-medium text-foreground bg-background hover:bg-muted transition-colors"
                        >
                            Go to Settings
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Format time for activity feed
    const formatActivityTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
        if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
        
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Get current week dates for mini calendar
    const getCurrentWeekDates = () => {
        const now = new Date();
        const day = now.getDay();
        const diff = now.getDate() - day; // Get Sunday of current week
        const weekStart = new Date(now.setDate(diff));
        
        const week = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(weekStart);
            date.setDate(weekStart.getDate() + i);
            week.push({
                day: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'][i],
                date: date.getDate(),
                isToday: date.toDateString() === new Date().toDateString()
            });
        }
        return week;
    };

    return (
        <div className="space-y-6">
            {/* Welcome Banner - Enhanced with profile picture and status */}
            <Card className="relative overflow-hidden border-0">
                {branding.agencyCoverImage ? (
                    <div className="relative">
                        <img
                            src={branding.agencyCoverImage}
                            alt="Agency Cover"
                            className="w-full h-48 object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40"></div>
                        <div className="absolute inset-0 p-6 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                {/* Profile Picture */}
                                <div className="flex-shrink-0">
                                    {user?.profileImage || branding?.userProfileImage ? (
                                        <img
                                            src={user?.profileImage || branding?.userProfileImage}
                                            alt={user?.name || 'User'}
                                            className="h-16 w-16 rounded-full object-cover border-4 border-white/20 shadow-lg"
                                        />
                                    ) : (
                                        <div className="h-16 w-16 rounded-full bg-white/20 backdrop-blur-sm border-4 border-white/20 shadow-lg flex items-center justify-center">
                                            <UserCircleIcon className="h-10 w-10 text-white" />
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <h1 className="text-3xl font-bold mb-1 text-white">
                                        Welcome back, {user?.name || 'User'}! 👋
                                    </h1>
                                    <p className="text-white/90 mb-2">
                                        Here's what's happening with {currentAgency?.name || 'your agency'} today.
                                    </p>
                                </div>
                            </div>
                            <div className="hidden md:block">
                                <Button variant="secondary" size="sm" className="bg-white/20 backdrop-blur-sm border-white/30 hover:bg-white/30 text-white">
                                    <PlusIcon className="h-4 w-4 mr-2" />
                                    Quick Add
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-gradient-to-r from-primary to-purple-600 p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                {/* Profile Picture */}
                                <div className="flex-shrink-0">
                                    {user?.profileImage || branding?.userProfileImage ? (
                                        <img
                                            src={user?.profileImage || branding?.userProfileImage}
                                            alt={user?.name || 'User'}
                                            className="h-16 w-16 rounded-full object-cover border-4 border-white/20 shadow-lg"
                                        />
                                    ) : (
                                        <div className="h-16 w-16 rounded-full bg-white/20 backdrop-blur-sm border-4 border-white/20 shadow-lg flex items-center justify-center">
                                            <UserCircleIcon className="h-10 w-10 text-white" />
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <h1 className="text-3xl font-bold mb-1 text-white">
                                        Welcome back, {user?.name || 'User'}! 👋
                                    </h1>
                                    <p className="text-white/90 mb-2">
                                        Here's what's happening with {currentAgency?.name || 'your agency'} today.
                                    </p>
                                </div>
                            </div>
                            <div className="hidden md:block">
                                <Button variant="secondary" size="sm" className="bg-white/20 backdrop-blur-sm border-white/30 hover:bg-white/30 text-white">
                                    <PlusIcon className="h-4 w-4 mr-2" />
                                    Quick Add
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Card>

            {/* Quick Access Cards - Top Section (like reference image) */}
            {canViewAnalytics && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Leads Card */}
                    <Card className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/leads')}>
                        <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${branding.primaryColor}15` }}>
                                <UserPlusIcon className="h-6 w-6" style={{ color: branding.primaryColor }} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-muted-foreground">Leads</p>
                                <p className="text-lg font-bold text-foreground">{additionalStats.leads?.total || 0} Total</p>
                            </div>
                        </div>
                    </Card>

                    {/* Tasks Card */}
                    <Card className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/tasks')}>
                        <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-lg bg-orange-100 dark:bg-orange-950/30 flex items-center justify-center">
                                <ClipboardDocumentListIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-muted-foreground">Tasks</p>
                                <p className="text-lg font-bold text-foreground">{dashboardData?.stats?.activeTasks || 0} Active</p>
                            </div>
                        </div>
                    </Card>

                    {/* Messages Card */}
                    <Card className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/messages')}>
                        <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-lg bg-purple-100 dark:bg-purple-950/30 flex items-center justify-center">
                                <ChatBubbleLeftRightIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-muted-foreground">Messages</p>
                                <p className="text-lg font-bold text-foreground">Unread</p>
                            </div>
                        </div>
                    </Card>

                    {/* Calendar Card */}
                    <Card className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/calendar')}>
                        <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-lg bg-cyan-100 dark:bg-cyan-950/30 flex items-center justify-center">
                                <CalendarIcon className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-muted-foreground">Events</p>
                                <p className="text-lg font-bold text-foreground">{additionalStats.upcomingEvents || 0} This Week</p>
                            </div>
                        </div>
                    </Card>
                </div>
            )}

            {/* Stats Grid - Only show if user has analytics permission */}
            {canViewAnalytics && dashboardData && (
                <div className="space-y-6">
                    {/* Sales Pipeline with Team Members Side by Side */}
                    {pipelineData && pipelineData.length > 0 && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Sales Pipeline - 2/3 width */}
                            <div className="lg:col-span-2">
                                <Card className="h-full flex flex-col">
                                    <CardHeader className="pb-3 flex-shrink-0">
                                        <div className="flex items-center justify-between">
                                            <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                                <ChartBarIcon className="h-5 w-5 text-primary" />
                                                Sales Pipeline
                                            </CardTitle>
                                            {/* Timeframe Selector */}
                                            <select 
                                                className="text-sm border border-input rounded-md px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
                                                value={pipelineTimeframe}
                                                onChange={(e) => setPipelineTimeframe(e.target.value)}
                                            >
                                                <option value="daily">Daily</option>
                                                <option value="weekly">Weekly</option>
                                                <option value="monthly">Monthly</option>
                                                <option value="yearly">Yearly</option>
                                            </select>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="flex-1 flex flex-col">
                                        {/* Pipeline Visualization */}
                                        <PipelineVisualization data={pipelineData} />
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Team Members - 1/3 width, same height as Sales Pipeline */}
                            <Card className="h-full flex flex-col">
                                <CardHeader className="pb-3 flex-shrink-0">
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                            <UserGroupIcon className="h-5 w-5 text-primary" />
                                            Team Members
                                        </CardTitle>
                                        <Link to="/team">
                                            <Button variant="outline" size="sm">
                                                View all
                                            </Button>
                                        </Link>
                                    </div>
                                </CardHeader>
                                <CardContent className="flex-1 overflow-hidden flex flex-col">
                                    <div className="space-y-3 flex-1 overflow-y-auto">
                                        {teamMembers.length > 0 ? (
                                            teamMembers.map((member) => (
                                                <div key={member._id} className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                                        {member.profileImage ? (
                                                            <img
                                                                src={member.profileImage}
                                                                alt={member.name || 'Team member'}
                                                                className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                                                            />
                                                        ) : (
                                                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                                <span className="text-sm font-medium text-primary">
                                                                    {member.name ? member.name.split(' ').map(n => n[0]).join('') : 'U'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-sm font-medium text-foreground truncate">{member.name || 'Unknown'}</p>
                                                            <p className="text-xs text-muted-foreground truncate">{member.email || 'No email'}</p>
                                                        </div>
                                                    </div>
                                                    <ChatBubbleLeftRightIcon 
                                                        className="h-4 w-4 text-muted-foreground hover:text-primary cursor-pointer flex-shrink-0 transition-colors" 
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            try {
                                                                // Check if direct/private channel exists with this member
                                                                const channelsRes = await crmApi.get('/collaboration/channels');
                                                                
                                                                let channelId = null;
                                                                if (channelsRes.data?.success && Array.isArray(channelsRes.data.data)) {
                                                                    // Find existing private/direct channel with this member
                                                                    const existingChannel = channelsRes.data.data.find(ch => {
                                                                        if (ch.type !== 'private' && ch.type !== 'direct') return false;
                                                                        const memberIds = ch.members.map(m => (m._id || m).toString());
                                                                        return memberIds.includes(member._id.toString()) && 
                                                                               memberIds.includes(user?._id?.toString() || '');
                                                                    });
                                                                    if (existingChannel) {
                                                                        channelId = existingChannel._id;
                                                                    }
                                                                }
                                                                
                                                                // If no channel exists, create a private channel
                                                                if (!channelId) {
                                                                    const createRes = await crmApi.post('/collaboration/channels', {
                                                                        name: `Direct: ${member.name || 'User'}`,
                                                                        type: 'private',
                                                                        memberIds: [member._id]
                                                                    });
                                                                    
                                                                    if (createRes.data?.success) {
                                                                        channelId = createRes.data.data._id;
                                                                    }
                                                                }
                                                                
                                                                // Navigate to collaboration hub with channel
                                                                if (channelId) {
                                                                    navigate(`/collaboration?channel=${channelId}`);
                                                                } else {
                                                                    navigate('/collaboration');
                                                                }
                                                            } catch (error) {
                                                                console.error('Error opening channel:', error);
                                                                navigate('/collaboration');
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center py-8 text-muted-foreground">
                                                <UserGroupIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                                <p className="text-sm">No team members</p>
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* Pipeline Stats Cards - Separate Row */}
                    {pipelineCards.length > 0 && (
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                    <ChartBarIcon className="h-5 w-5 text-primary" />
                                    Pipeline Overview
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {pipelineCards.map((stat) => (
                                        <SmartDashboardCard
                                            key={stat.name}
                                            title={stat.name}
                                            value={stat.value.toLocaleString()}
                                            change={stat.change}
                                            changeType={stat.changeType}
                                            icon={stat.icon}
                                            color={stat.color || 'primary'}
                                            action={() => window.location.href = stat.link}
                                            trendData={stat.trendData}
                                        />
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Service Delivery Section */}
                    {serviceCards.length > 0 && (
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                    <ClipboardDocumentListIcon className="h-5 w-5 text-primary" />
                                    Service Delivery
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {serviceCards.map((stat) => (
                                        <SmartDashboardCard
                                            key={stat.name}
                                            title={stat.name}
                                            value={stat.value.toLocaleString()}
                                            change={stat.change}
                                            changeType={stat.changeType}
                                            icon={stat.icon}
                                            color={stat.color || 'primary'}
                                            action={() => window.location.href = stat.link}
                                            trendData={stat.trendData}
                                            metricType={stat.metricType}
                                            chartType={stat.chartType}
                                        />
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Activity & Engagement Section */}
                    {activityCards.length > 0 && (
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                    <EyeIcon className="h-5 w-5 text-primary" />
                                    Activity & Engagement
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {activityCards.map((stat) => (
                                        <SmartDashboardCard
                                            key={stat.name}
                                            title={stat.name}
                                            value={stat.value.toLocaleString()}
                                            change={stat.change}
                                            changeType={stat.changeType}
                                            icon={stat.icon}
                                            color={stat.color || 'primary'}
                                            action={() => window.location.href = stat.link}
                                            trendData={stat.trendData}
                                            metricType={stat.metricType}
                                            chartType={stat.chartType}
                                        />
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Collaboration Hub - Recent Messages */}
                    {collaborationOverview && (
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                        <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary" />
                                        Collaboration Hub
                                    </CardTitle>
                                    <Link to="/collaboration">
                                        <Button variant="outline" size="sm">
                                            View all
                                        </Button>
                                    </Link>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                                        <div className="text-2xl font-bold text-foreground">{collaborationOverview.activeChannels || 0}</div>
                                        <div className="text-sm text-muted-foreground">Active Channels</div>
                                    </div>
                                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                                        <div className="text-2xl font-bold text-foreground">{collaborationOverview.unreadMessages || 0}</div>
                                        <div className="text-sm text-muted-foreground">Unread Messages</div>
                                    </div>
                                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                                        <div className="text-2xl font-bold text-foreground">{collaborationOverview.totalMessages || 0}</div>
                                        <div className="text-sm text-muted-foreground">Total Messages</div>
                                    </div>
                                </div>
                                
                                {/* Recent Channel Messages */}
                                {recentChannelMessages.length > 0 && (
                                    <div className="space-y-3">
                                        <h3 className="text-sm font-semibold text-foreground mb-3">Recent Messages</h3>
                                        {recentChannelMessages.map((message) => (
                                            <div key={message._id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                                                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                    <ChatBubbleLeftRightIcon className="h-4 w-4 text-primary" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <p className="text-sm font-medium text-foreground truncate">
                                                            {message.author?.name || 'Unknown'}
                                                        </p>
                                                        <span className="text-xs text-muted-foreground">in</span>
                                                        <p className="text-xs font-medium text-primary truncate">
                                                            {message.channelName}
                                                        </p>
                                                        {message.channelType === 'private' && (
                                                            <span className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded">Private</span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground truncate">
                                                        {message.content?.text || 'No content'}
                                                    </p>
                                                    <p className="text-[10px] text-muted-foreground mt-1">
                                                        {formatActivityTime(new Date(message.createdAt))}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* Digest Section */}
            {dashboardData?.digest && (
                <Card className="p-6">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Since Last Login</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="text-center">
                            <div className="text-2xl font-bold" style={{ color: branding?.primaryColor || '#3b82f6' }}>{dashboardData.digest.counts.newActivities}</div>
                            <div className="text-sm text-muted-foreground">New Activities</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-orange-600">{dashboardData.digest.counts.atRiskClients}</div>
                            <div className="text-sm text-muted-foreground">At-Risk Clients</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">{dashboardData.digest.counts.tasksDueThisWeek}</div>
                            <div className="text-sm text-muted-foreground">Tasks Due This Week</div>
                        </div>
                    </div>
                </Card>
            )}

            {/* Charts Row */}
            {canViewAnalytics && dashboardData && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Client Status Distribution */}
                    <EnhancedDonutChart
                        data={dashboardData?.clientStatusData || []}
                        title="Client Status Distribution"
                        subtitle="Overview of client engagement levels"
                        height={350}
                        helpText="This chart shows the distribution of your clients across different status levels. Active clients are currently engaged, prospects are potential clients, completed clients have finished their relocation process, and inactive clients are no longer active."
                        onSegmentClick={(data) => {
                            // Navigate to filtered client list
                            window.location.href = `/clients?status=${data.name.toLowerCase()}`;
                        }}
                    />

                    {/* Task Status Distribution */}
                    <EnhancedDonutChart
                        data={dashboardData?.taskStatusData || []}
                        title="Task Status Overview"
                        subtitle="Current state of all tasks in your pipeline"
                        height={350}
                        helpText="This chart displays the current status of all tasks. Completed tasks are finished, in-progress tasks are being worked on, pending tasks are waiting to start, and overdue tasks need immediate attention."
                        onSegmentClick={(data) => {
                            // Navigate to filtered task list
                            window.location.href = `/tasks?status=${data.name.toLowerCase().replace(' ', '-')}`;
                        }}
                    />
                </div>
            )}

            {/* Simple Dashboard for Users Without Analytics Permission */}
            {!canViewAnalytics && (
                <Card className="p-6">
                    <div className="text-center py-8">
                        <ChartBarIcon className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                            Analytics Access Required
                        </h3>
                        <p className="text-muted-foreground mb-4">
                            You don't have permission to view detailed analytics. Use the Quick Actions below to access your work.
                        </p>
                    </div>
                </Card>
            )}

            {/* Recent Clients & Tasks Row */}
            {canViewAnalytics && dashboardData && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Recent Clients */}
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-semibold text-foreground">Recent Clients</h2>
                            <Link to="/clients">
                                <Button variant="outline" size="sm">
                                    View all
                                </Button>
                            </Link>
                        </div>
                        <div className="space-y-4">
                            {dashboardData?.recentClients && dashboardData.recentClients.length > 0 ? (
                                dashboardData.recentClients.slice(0, 5).map((client) => (
                                    <div key={client.id || client._id} className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <div className="h-10 w-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                                                <span className="text-sm font-medium text-primary-foreground">
                                                    {(() => {
                                                        const fullName = `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.name || '';
                                                        return fullName ? fullName.split(' ').map(n => n[0]).join('') : 'N/A';
                                                    })()}
                                                </span>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-foreground truncate">{`${client.firstName || ''} ${client.lastName || ''}`.trim() || client.name || 'Unknown Client'}</p>
                                                <p className="text-xs text-muted-foreground truncate">{client.email || 'No email'}</p>
                                            </div>
                                        </div>
                                        <StatusBadge 
                                            status={client.status} 
                                            type="client" 
                                            size="sm"
                                            showIcon={false}
                                            showDot={true}
                                        />
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <UserGroupIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                    <p>No recent clients</p>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Recent Tasks */}
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-semibold text-foreground">Recent Tasks</h2>
                            <Link to="/tasks">
                                <Button variant="outline" size="sm">
                                    View all
                                </Button>
                            </Link>
                        </div>
                        <div className="space-y-4">
                            {dashboardData?.recentTasks && dashboardData.recentTasks.length > 0 ? (
                                dashboardData.recentTasks.slice(0, 5).map((task) => (
                                    <div key={task.id || task._id} className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3 min-w-0">
                                            <div className="h-10 w-10 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                                                <ClipboardDocumentListIcon className="h-5 w-5 text-secondary-foreground" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-foreground truncate">{task.title || 'Untitled Task'}</p>
                                                <p className="text-xs text-muted-foreground truncate">{task.clientId?.name || 'No client assigned'}</p>
                                            </div>
                                        </div>
                                        <StatusBadge 
                                            status={task.priority} 
                                            type="priority" 
                                            size="sm"
                                            showIcon={false}
                                            showDot={true}
                                        />
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <ClipboardDocumentListIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                    <p>No recent tasks</p>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            )}

            {/* Recent Activity - Below Recent Clients & Tasks */}
            {canViewAnalytics && dashboardData && (
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-foreground">Recent Activity</h2>
                    </div>

                    {/* Activity List */}
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                        {recentActivities.length > 0 ? (
                            recentActivities.map((activity) => {
                                const Icon = activity.icon;
                                return (
                                    <div key={activity.id} className="flex items-start gap-3">
                                        <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${activity.color.replace('text-', 'bg-').replace('-600', '-100 dark:bg-').replace('-400', '-950/30')}`}>
                                            <Icon className={`h-4 w-4 ${activity.color}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-foreground truncate">{activity.title}</p>
                                            <p className="text-xs text-muted-foreground truncate">{activity.description}</p>
                                            <p className="text-[10px] text-muted-foreground mt-0.5">{formatActivityTime(activity.timestamp)}</p>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                <ClockIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                <p className="text-sm">No recent activity</p>
                            </div>
                        )}
                    </div>
                </Card>
            )}

            {/* Quick Actions */}
            <Card className="p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {canCreateClient && (
                    <Button 
                        variant="outline" 
                        className="h-20 flex-col hover:opacity-90 transition-opacity"
                        onClick={() => navigate('/clients')}
                        style={{ 
                            borderColor: branding.primaryColor,
                            color: branding.primaryColor
                        }}
                    >
                        <UserGroupIcon className="h-6 w-6 mb-2" />
                        Add Client
                    </Button>
                    )}
                    {canCreateTask && (
                    <Button 
                        variant="outline" 
                        className="h-20 flex-col hover:opacity-90 transition-opacity"
                        onClick={() => navigate('/tasks')}
                        style={{ 
                            borderColor: branding.primaryColor,
                            color: branding.primaryColor
                        }}
                    >
                        <ClipboardDocumentListIcon className="h-6 w-6 mb-2" />
                        Create Task
                    </Button>
                    )}
                    {canScheduleMeeting && (
                    <Button 
                        variant="outline" 
                        className="h-20 flex-col hover:opacity-90 transition-opacity"
                        onClick={() => navigate('/calendar')}
                        style={{ 
                            borderColor: branding.primaryColor,
                            color: branding.primaryColor
                        }}
                    >
                        <CalendarIcon className="h-6 w-6 mb-2" />
                        Schedule Meeting
                    </Button>
                    )}
                    {canViewReports && (
                    <Button 
                        variant="outline" 
                        className="h-20 flex-col hover:opacity-90 transition-opacity"
                        onClick={() => navigate('/analytics')}
                        style={{ 
                            borderColor: branding.primaryColor,
                            color: branding.primaryColor
                        }}
                    >
                        <ChartBarIcon className="h-6 w-6 mb-2" />
                        View Reports
                    </Button>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default Dashboard;


